/**
 * PDF Viewer Component - Native PDF rendering with facet highlights
 *
 * Features:
 * - Native PDF rendering using react-pdf
 * - SVG overlay for text and visual facet highlights
 * - Page navigation and zoom controls
 * - Click-to-select facet interaction
 */

import { useState, useCallback, useMemo } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Image as ImageIcon,
} from 'lucide-react'
import type { DetectedFacet } from '@/lib/api/client'
import type { VisualFacet, SourceImage } from '@/lib/api/client'

// Configure PDF.js worker - use CDN to match react-pdf's bundled pdfjs version
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

// ============================================================================
// TYPES
// ============================================================================

interface PdfViewerProps {
  fileData: ArrayBuffer | string  // PDF binary or base64
  textFacets?: DetectedFacet[]
  visualFacets?: VisualFacet[]
  images?: SourceImage[]
  selectedFacetId?: string | null
  onFacetClick?: (facetId: string, type: 'text' | 'visual') => void
  className?: string
}

interface HighlightRect {
  x: number
  y: number
  width: number
  height: number
  facetId: string
  color: string
  facetType: 'text' | 'visual'
}

// Facet color map based on category
const FACET_COLORS: Record<string, string> = {
  market: '#00b894',
  business: '#4a90a4',
  solution: '#d4a020',
  insights: '#6c757d',
}

function getFacetColor(facetTypeId: string): string {
  const lowerType = facetTypeId.toLowerCase()
  if (lowerType.includes('customer') || lowerType.includes('problem') ||
      lowerType.includes('pain') || lowerType.includes('competitor')) {
    return FACET_COLORS.market
  }
  if (lowerType.includes('decision') || lowerType.includes('strategy') ||
      lowerType.includes('revenue') || lowerType.includes('metric')) {
    return FACET_COLORS.business
  }
  if (lowerType.includes('feature') || lowerType.includes('product') ||
      lowerType.includes('solution') || lowerType.includes('milestone')) {
    return FACET_COLORS.solution
  }
  return FACET_COLORS.insights
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function PdfViewer({
  fileData,
  textFacets = [],
  visualFacets = [],
  images = [],
  selectedFacetId,
  onFacetClick,
  className,
}: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [scale, setScale] = useState<number>(1.0)
  const [pageWidth, setPageWidth] = useState<number>(0)
  const [pageHeight, setPageHeight] = useState<number>(0)

  // Convert file data to a format react-pdf can use
  const pdfSource = useMemo(() => {
    if (typeof fileData === 'string') {
      // Assume base64
      return { data: atob(fileData) }
    }
    return { data: fileData }
  }, [fileData])

  // Group visual facets by page
  const visualFacetsByPage = useMemo(() => {
    const map = new Map<number, VisualFacet[]>()
    for (const facet of visualFacets) {
      const image = images.find(i => i.id === facet.imageId)
      if (image?.pageNumber) {
        const existing = map.get(image.pageNumber) || []
        map.set(image.pageNumber, [...existing, facet])
      }
    }
    return map
  }, [visualFacets, images])

  // Get images for current page
  const currentPageImages = useMemo(() => {
    return images.filter(img => img.pageNumber === currentPage)
  }, [images, currentPage])

  // Get visual facets for current page
  const currentPageVisualFacets = visualFacetsByPage.get(currentPage) || []

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
  }, [])

  const onPageLoadSuccess = useCallback((page: { width: number; height: number }) => {
    setPageWidth(page.width * scale)
    setPageHeight(page.height * scale)
  }, [scale])

  const goToPrevPage = () => setCurrentPage(p => Math.max(1, p - 1))
  const goToNextPage = () => setCurrentPage(p => Math.min(numPages, p + 1))
  const zoomIn = () => setScale(s => Math.min(2.0, s + 0.1))
  const zoomOut = () => setScale(s => Math.max(0.5, s - 0.1))
  const fitToWidth = () => setScale(1.0)

  return (
    <div className={cn('flex flex-col h-full bg-warm-100', className)}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 p-2 border-b border-warm-200 bg-warm-50">
        {/* Navigation */}
        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={goToPrevPage}
            disabled={currentPage <= 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-text-secondary min-w-24 text-center">
            Page {currentPage} of {numPages}
          </span>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={goToNextPage}
            disabled={currentPage >= numPages}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Zoom controls */}
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={zoomOut}>
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-sm text-text-secondary w-12 text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={zoomIn}>
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={fitToWidth}>
            <Maximize2 className="w-4 h-4" />
          </Button>
        </div>

        {/* Visual facet indicator */}
        {currentPageImages.length > 0 && (
          <div className="flex items-center gap-1 text-xs text-text-muted">
            <ImageIcon className="w-3.5 h-3.5" />
            {currentPageImages.length} image{currentPageImages.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* PDF Content */}
      <div className="flex-1 overflow-auto p-4">
        <div className="flex justify-center">
          <div className="relative shadow-lg bg-white">
            <Document
              file={pdfSource}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={
                <div className="flex items-center justify-center h-96 w-96">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-solution" />
                </div>
              }
              error={
                <div className="flex items-center justify-center h-96 w-96 text-red-500">
                  Failed to load PDF
                </div>
              }
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                onLoadSuccess={onPageLoadSuccess}
                renderTextLayer={true}
                renderAnnotationLayer={false}
              />
            </Document>

            {/* Visual Facet Overlay */}
            {currentPageVisualFacets.length > 0 && pageWidth > 0 && (
              <svg
                className="absolute inset-0 pointer-events-none"
                width={pageWidth}
                height={pageHeight}
                style={{ left: 0, top: 0 }}
              >
                {currentPageVisualFacets.map((facet, idx) => {
                  if (!facet.bboxX || !facet.bboxY) return null

                  const x = (facet.bboxX / 100) * pageWidth
                  const y = (facet.bboxY / 100) * pageHeight
                  const w = ((facet.bboxWidth || 10) / 100) * pageWidth
                  const h = ((facet.bboxHeight || 10) / 100) * pageHeight
                  const color = getFacetColor(facet.facetType)
                  const isSelected = facet.id === selectedFacetId

                  return (
                    <g key={facet.id || idx}>
                      <rect
                        x={x}
                        y={y}
                        width={w}
                        height={h}
                        fill={color}
                        fillOpacity={isSelected ? 0.3 : 0.15}
                        stroke={color}
                        strokeWidth={isSelected ? 3 : 2}
                        strokeDasharray={isSelected ? '0' : '4 2'}
                        className="pointer-events-auto cursor-pointer transition-all"
                        onClick={() => onFacetClick?.(facet.id, 'visual')}
                      />
                      {/* Label */}
                      <text
                        x={x + 4}
                        y={y - 4}
                        fontSize={10}
                        fill={color}
                        className="font-medium pointer-events-none"
                      >
                        {facet.title.slice(0, 30)}
                      </text>
                    </g>
                  )
                })}
              </svg>
            )}
          </div>
        </div>
      </div>

      {/* Facet Legend (if facets exist) */}
      {(textFacets.length > 0 || visualFacets.length > 0) && (
        <div className="flex-shrink-0 p-2 border-t border-warm-200 bg-warm-50/80">
          <div className="flex items-center gap-4 text-xs text-text-muted">
            <span>Facets:</span>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: FACET_COLORS.market }} />
              <span>Market ({textFacets.filter(f => getFacetColor(f.facetTypeId) === FACET_COLORS.market).length})</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: FACET_COLORS.business }} />
              <span>Business ({textFacets.filter(f => getFacetColor(f.facetTypeId) === FACET_COLORS.business).length})</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: FACET_COLORS.solution }} />
              <span>Solution ({textFacets.filter(f => getFacetColor(f.facetTypeId) === FACET_COLORS.solution).length})</span>
            </div>
            {visualFacets.length > 0 && (
              <div className="flex items-center gap-1 ml-2 pl-2 border-l border-warm-300">
                <ImageIcon className="w-3 h-3" />
                <span>{visualFacets.length} visual</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default PdfViewer
